import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

import ContactHistory from '../pages/LeadsPage/ContactHistory.js';
import EntityFiles from './EntityFiles.js';

import firebase from '../utilities/firebase';
import history from '../utilities/history';
import { StateManager } from '../utilities/stateManager';
import constants from '../utilities/constants';
import { useAutoZ, ModalStackProvider } from '../utilities/ModalStackContext';
import { useTallDialogSizing } from '../utilities/dialogSizing';

const VISIBLE_TABS = ['details', 'files'];
const TAB_LABELS = { details: 'Details', files: 'Files' };

const Loading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

export default function LeadModal({ open, leadId, onClose }) {
  const myZ = useAutoZ(open, leadId);
  const { fullScreen, paperSx } = useTallDialogSizing({ height: '95vh', margin: 1.5, radius: 2 });
  const theme = useTheme();
  const id = leadId;

  const [loading, setLoading] = React.useState(true);
  const [lead, setLead] = React.useState(null);
  const [car, setCar] = React.useState(null);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState(0);

  React.useEffect(() => {
    setLead(null);
    setCar(null);

    if (!id) {
      setLoading(false);
      return;
    }

    const tabParam = new URL(window.location.href).searchParams.get('leadTab');
    const idx = VISIBLE_TABS.indexOf(tabParam);
    setActiveTab(idx >= 0 ? idx : 0);

    let cancelled = false;
    setLoading(true);

    async function fetchData() {
      const db = firebase.firestore();
      try {
        const leadDoc = await db.doc('leads/' + id).get();
        if (cancelled) return;
        if (!leadDoc.exists) {
          setLead(null);
          return;
        }
        const leadData = { id: leadDoc.id, ...leadDoc.data() };
        setLead(leadData);

        if (leadData.stock) {
          try {
            const carSnap = await db.doc('cars/' + leadData.stock).get();
            if (!cancelled && carSnap.exists) setCar({ id: carSnap.id, ...carSnap.data() });
          } catch (error) {
            // Non-fatal — the lead still renders without the car join.
            console.error('Error loading car for lead:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching lead:', error);
        StateManager.setAlertAndOpen('Error loading lead', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [id, refreshTrigger]);

  const handleDataRefresh = () => setRefreshTrigger((prev) => prev + 1);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    const url = new URL(window.location.href);
    url.searchParams.set('leadTab', VISIBLE_TABS[newValue]);
    history.replace(url.pathname + '?' + url.searchParams.toString());
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const repName = (() => {
    if (!lead) return '';
    const rep = constants.sales.find((s) => s.id === lead.sales_id);
    return rep ? rep.name : constants.userNames[lead.sales_id] || 'Unassigned';
  })();

  const carTitle = car
    ? [car.year, car.make, car.model, car.trim === 'Base' ? '' : car.trim].filter(Boolean).join(' ')
    : lead?.car || '';

  const displayName = lead?.name || 'Lead';
  const activeTabKey = VISIBLE_TABS[activeTab];

  return (
    <ModalStackProvider value={myZ}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        sx={{ zIndex: myZ }}
        PaperProps={{
          sx: {
            ...paperSx,
            overflow: 'hidden',
          },
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {id ? displayName : 'Loading…'}
          </Typography>
          {lead?.status && (
            <Chip size="small" label={lead.status} sx={{ textTransform: 'capitalize' }} />
          )}
          {lead && (
            <Chip
              size="small"
              variant="outlined"
              label={lead.type === 'sell' ? 'Seller' : 'Buyer'}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {car?.stock && (
            <Tooltip title="Open car">
              <Button
                size="small"
                variant="outlined"
                startIcon={<DirectionsCarIcon />}
                onClick={() => StateManager.openCarModal(car.stock)}
              >
                {car.stock}
              </Button>
            </Tooltip>
          )}
          <IconButton onClick={handleClose} size="small" sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Sub-header */}
        {lead && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              bgcolor: theme.palette.grey[50],
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            {carTitle && (
              <Typography variant="body2" color="text.secondary">
                {carTitle}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Rep: <strong>{repName}</strong>
            </Typography>
            {lead.source && (
              <Typography variant="body2" color="text.disabled">
                Source: {lead.source}
              </Typography>
            )}
          </Box>
        )}

        {/* Tab bar */}
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0, px: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: '0.8rem', textTransform: 'none', fontWeight: 500 },
              '& .Mui-selected': { fontWeight: 700 },
            }}
          >
            {VISIBLE_TABS.map((key) => (
              <Tab key={key} label={TAB_LABELS[key]} />
            ))}
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Box sx={{ p: 2 }}>
            {loading || !lead ? (
              loading ? <Loading /> : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
                  Lead not found.
                </Typography>
              )
            ) : activeTabKey === 'details' ? (
              <ContactHistory lead={lead} onUpdate={handleDataRefresh} />
            ) : (
              <EntityFiles type="lead" id={id} label={displayName} legacyStock={id} />
            )}
          </Box>
        </Box>
      </Dialog>
    </ModalStackProvider>
  );
}
